/**
 * Markdown-style emphasis inside sanitized HTML. Workfront's rich-text fields carry
 * `**bold**` and `*italic*` as literal asterisks; this turns them into `<strong>` and
 * `<em>`.
 *
 * It runs on markup that is ALREADY sanitized and only ever adds elements built with
 * `textContent`, so the matched text can never become markup. Emphasis does not span
 * tags: each text node is read on its own.
 */

/** `**bold**` (group 1) or `*italic*` (group 2), with no space just inside the marks. */
const EMPHASIS = /\*\*(?=\S)([^*]+?)(?<=\S)\*\*|(?<![*\w])\*(?=\S)([^*]+?)(?<=\S)\*(?![*\w])/g

function emphasize(text: Text): void {
	const value = text.data
	const parts: Node[] = []
	let last = 0
	for (const match of value.matchAll(EMPHASIS)) {
		if (match.index > last) parts.push(document.createTextNode(value.slice(last, match.index)))
		const bold = match[1] !== undefined
		const element = document.createElement(bold ? 'strong' : 'em')
		element.textContent = bold ? match[1] : match[2]
		parts.push(element)
		last = match.index + match[0].length
	}
	if (parts.length === 0) return
	if (last < value.length) parts.push(document.createTextNode(value.slice(last)))
	text.replaceWith(...parts)
}

/** Sanitized HTML with its markdown-style `**bold**` and `*italic*` rendered as emphasis. */
export function withMarkdownEmphasis(html: string): string {
	if (!html.includes('*')) return html
	const template = document.createElement('template')
	template.innerHTML = html
	const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT)
	const texts: Text[] = []
	while (walker.nextNode()) texts.push(walker.currentNode as Text)
	for (const text of texts) emphasize(text)
	return template.innerHTML
}
